import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  AppBar, 
  Toolbar, 
  IconButton, 
  Button,
  Chip,
  Avatar,
  useMediaQuery,
  useTheme,
  Fab,
  Zoom
} from '@mui/material';
import { 
  ExitToApp as LogoutIcon,
  LocationOn as LocationIcon,
  Notifications as AlertIcon,
  Person as PersonIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Home as HomeIcon,
  Fingerprint as FingerprintIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useAssets } from '../../contexts/AssetsContext';
import { useAlerts } from '../../contexts/AlertsContext';
import ClientMap from '../../components/client/ClientMap';
import BottomNavigation from '../../components/client/BottomNavigation';
import ProfilePage from '../../components/client/ProfilePage';
import AssetsListMobile from '../../components/client/AssetsListMobile';
import AlertsListMobile from '../../components/client/AlertsListMobile';
import AddAssetForm from '../../components/client/AddAssetForm';
import PanicAlertDialog from '../../components/client/PanicAlertDialog';
import ResolveAlertDialog from '../../components/client/ResolveAlertDialog';

const ClientDashboard = () => {
  const { currentUser, logout } = useAuth();
  const { getUserAssets } = useAssets();
  const { getUserAlerts } = useAlerts();
  const [userAssets, setUserAssets] = useState([]);
  const [userAlerts, setUserAlerts] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [navValue, setNavValue] = useState(0);
  const [panicAlertOpen, setPanicAlertOpen] = useState(false);
  const [resolveAlertOpen, setResolveAlertOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get user assets and alerts on component mount
  useEffect(() => {
    if (currentUser) {
      const assets = getUserAssets(currentUser.id);
      setUserAssets(assets);
      
      const alerts = getUserAlerts(currentUser.id);
      setUserAlerts(alerts);
      
      // Select the first asset by default if available
      if (assets.length > 0 && !selectedAssetId) {
        setSelectedAssetId(assets[0].id);
      }
    }
  }, [currentUser, getUserAssets, getUserAlerts, selectedAssetId]);

  // Get the selected asset object
  const selectedAsset = userAssets.find(asset => asset.id === selectedAssetId) || null;
  
  // Count active alerts
  const activeAlertsCount = userAlerts.filter(alert => alert.status === 'active').length;
  
  // Handle bottom navigation change
  const handleNavChange = (event, newValue) => {
    setNavValue(newValue);
  };
  
  // Handle panic button click
  const handlePanicButtonClick = () => {
    setPanicAlertOpen(true);
  };
  
  // Handle alert selection for resolution
  const handleAlertSelect = (alert) => {
    setSelectedAlert(alert);
    setResolveAlertOpen(true);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* App Bar */}
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ShieldIcon sx={{ color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: 'primary.main' }}>
              SentinelEye
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {activeAlertsCount > 0 && (
              <Chip
                label={`${activeAlertsCount} Alert${activeAlertsCount > 1 ? 's' : ''}`}
                color="error"
                size="small"
                sx={{ mr: 1 }}
                onClick={() => setNavValue(3)} // Navigate to Alerts tab
              />
            )}
            <Chip
              avatar={<Avatar sx={{ bgcolor: 'primary.50' }}><PersonIcon fontSize="small" /></Avatar>}
              label={currentUser?.username || 'client1'}
              variant="outlined"
              sx={{ borderColor: 'gray.200' }}
              onClick={() => setNavValue(4)} // Navigate to Profile tab
            />
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Main Content */}
      <Container maxWidth="sm" sx={{ mt: 2, mb: 8, flexGrow: 1 }}>
        {/* Home Screen */}
        {navValue === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                Welcome, {currentUser?.name?.split(' ')[0] || 'Client'}
              </Typography>
              <Button 
                variant="contained" 
                color="error" 
                startIcon={<WarningIcon />}
                onClick={handlePanicButtonClick}
              >
                PANIC
              </Button>
            </Box>
            
            <Paper sx={{ mb: 3, p: 0, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Asset Location
                </Typography>
              </Box>
              <Box sx={{ height: 250 }}>
                {selectedAsset && (
                  <ClientMap 
                    assetLocation={selectedAsset.location} 
                    geofenceRadius={500}
                  />
                )}
              </Box>
            </Paper>
            
            {/* Quick Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50', height: '100%' }}>
                  <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                    {userAssets.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tracked Assets
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: activeAlertsCount > 0 ? 'error.50' : 'success.50', height: '100%' }}>
                  <Typography 
                    variant="h4" 
                    color={activeAlertsCount > 0 ? 'error.main' : 'success.main'} 
                    sx={{ fontWeight: 600 }}
                  >
                    {activeAlertsCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Alerts
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            
            {/* Recent Alerts Preview */}
            {activeAlertsCount > 0 && (
              <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Recent Alerts
                  </Typography>
                  <Button size="small" onClick={() => setNavValue(3)}>View All</Button>
                </Box>
                <AlertsListMobile onSelectAlert={handleAlertSelect} />
              </Paper>
            )}
          </Box>
        )}
        
        {/* Map Screen */}
        {navValue === 1 && (
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
              Asset Tracking Map
            </Typography>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
              <Box sx={{ height: 'calc(100vh - 200px)', minHeight: 400 }}>
                {selectedAsset && (
                  <ClientMap 
                    assetLocation={selectedAsset.location} 
                    geofenceRadius={500}
                  />
                )}
              </Box>
            </Paper>
          </Box>
        )}
        
        {/* Assets Screen */}
        {navValue === 2 && (
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
              My Assets
            </Typography>
            <AssetsListMobile onSelectAsset={setSelectedAssetId} />
          </Box>
        )}
        
        {/* Alerts Screen */}
        {navValue === 3 && (
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
              Alerts
            </Typography>
            <AlertsListMobile onSelectAlert={handleAlertSelect} />
          </Box>
        )}
        
        {/* Profile Screen */}
        {navValue === 4 && (
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
              My Profile
            </Typography>
            <ProfilePage />
          </Box>
        )}
      </Container>
      
      {/* Bottom Navigation */}
      <BottomNavigation value={navValue} onChange={handleNavChange} />
      
      {/* Add Asset FAB - shown only on Assets tab */}
      <Zoom in={navValue === 2}>
        <Fab 
          color="primary" 
          aria-label="add asset"
          sx={{ position: 'fixed', bottom: 80, right: 16 }}
          onClick={() => setNavValue(5)} // Navigate to Add Asset form
        >
          <AddIcon />
        </Fab>
      </Zoom>
      
      {/* Add Asset Form */}
      {navValue === 5 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={() => setNavValue(2)} sx={{ mr: 1 }}>
              <HomeIcon />
            </IconButton>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              Add New Asset
            </Typography>
          </Box>
          <AddAssetForm />
        </Box>
      )}
      
      {/* Enhanced Panic Alert Dialog */}
      <PanicAlertDialog 
        open={panicAlertOpen} 
        onClose={() => setPanicAlertOpen(false)} 
      />
      
      {/* Resolve Alert Dialog */}
      <ResolveAlertDialog 
        open={resolveAlertOpen} 
        onClose={() => setResolveAlertOpen(false)}
        alert={selectedAlert}
      />
    </Box>
  );
};

export default ClientDashboard;
